import React, { createContext, useState, useContext, useEffect } from "react";
import { isTablet } from "react-device-detect";
import configuracionService from "@/api/configuracionService";
import { getUserId } from "../../utils/authStorage";

// Crear el contexto
const LayoutContext = createContext();

export const LayoutProvider = ({ children }) => {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isBottomNavVisible, setIsBottomNavVisible] = useState(true);
  const [isCel, setIsCel] = useState(false);
  // Nuevo estado para sidebar colapsada
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    const saved = localStorage.getItem("darkTheme");
    return saved !== null ? saved === "true" : true;
  });

  // Estados para detección de orientación y tablet
  const [isTabletDevice, setIsTabletDevice] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);

  // Función para detectar tamaño de pantalla, tipo de dispositivo y orientación
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      // Detectar si es móvil (< 768px)
      setIsCel(width < 768);

      // Detectar si es tablet usando react-device-detect y dimensiones
      const isTabletByLibrary = isTablet;
      const isTabletByDimensions =
        (width >= 768 && width <= 1024) || (height >= 768 && height <= 1024);
      const detectedTablet = isTabletByLibrary || isTabletByDimensions;
      setIsTabletDevice(detectedTablet);

      // Detectar orientación
      const landscape = width > height;
      setIsLandscape(landscape);

      // Si la pantalla es muy pequeña, colapsar automáticamente
      if (width < 1024 && width >= 768) {
        setIsSidebarCollapsed(true);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    // Intentar recuperar la preferencia del usuario del localStorage
    const savedCollapsedState = localStorage.getItem("sidebarCollapsed");
    if (savedCollapsedState !== null) {
      setIsSidebarCollapsed(savedCollapsedState === "true");
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  // Funciones para manipular la visibilidad
  const showSidebar = () => setIsSidebarVisible(true);
  const hideSidebar = () => setIsSidebarVisible(false);
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const showBottomNav = () => setIsBottomNavVisible(true);
  const hideBottomNav = () => setIsBottomNavVisible(false);
  const toggleBottomNav = () => setIsBottomNavVisible(!isBottomNavVisible);

  // Nuevas funciones para manejar el estado colapsado
  const collapseSidebar = () => {
    setIsSidebarCollapsed(true);
    localStorage.setItem("sidebarCollapsed", "true");
  };

  const expandSidebar = () => {
    setIsSidebarCollapsed(false);
    localStorage.setItem("sidebarCollapsed", "false");
  };

  const toggleSidebarCollapse = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", String(newState));
  };

  // Función para cambiar entre tema claro y oscuro
  const toggleTheme = () => {
    // Actualizar el estado con el nuevo valor
    const newThemeValue = !isDarkTheme;
    console.log(
      `LayoutContext: Cambiando tema a ${newThemeValue ? "oscuro" : "claro"}`,
    );

    // Establecer el estado
    setIsDarkTheme(newThemeValue);

    // Almacenar preferencia en localStorage
    localStorage.setItem("darkTheme", String(newThemeValue));

    // Actualizar inmediatamente el DOM para mayor consistencia
    if (newThemeValue) {
      document.documentElement.classList.add("dark-theme");
      document.body.classList.add("dark-theme");
    } else {
      document.documentElement.classList.remove("dark-theme");
      document.body.classList.remove("dark-theme");
    }

    // Disparar un evento para notificar a otros componentes
    const event = new Event("themeChanged");
    window.dispatchEvent(event);

    // Simular un cambio en localStorage para activar listeners
    const storageEvent = new StorageEvent("storage", {
      key: "darkTheme",
      newValue: String(newThemeValue),
      oldValue: String(!newThemeValue),
      storageArea: localStorage,
    });
    window.dispatchEvent(storageEvent);

    // Persistir preferencia en BD si el usuario está autenticado
    const userId = getUserId();
    if (userId) {
      configuracionService
        .updateConfiguracion(userId, { temaoscuro: newThemeValue })
        .catch(() => {});
    }
  };

  return (
    <LayoutContext.Provider
      value={{
        isSidebarVisible,
        isBottomNavVisible,
        showSidebar,
        hideSidebar,
        toggleSidebar,
        showBottomNav,
        hideBottomNav,
        toggleBottomNav,
        isCel,
        // Nuevos valores para el estado colapsado
        isSidebarCollapsed,
        collapseSidebar,
        expandSidebar,
        toggleSidebarCollapse,
        isDarkTheme,
        toggleTheme,
        // Nuevos valores para tablet y orientación
        isTabletDevice,
        isLandscape,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout debe ser usado dentro de un LayoutProvider");
  }
  return context;
};

export default LayoutContext;
